import { CommonModule } from "@angular/common";
import { Component, inject, computed, signal } from "@angular/core";
import { rxResource } from "@angular/core/rxjs-interop";
import { of, tap } from "rxjs";
import { ReactiveFormsModule } from "@angular/forms";
import { TranslocoDirective, TranslocoService } from "@ngneat/transloco";
import { LoyaltySubscription, SubscriptionSavings } from "../../core/models/loyalty/loyalty-subscription.model";
import { ModalService, ModalName } from "../../core/services/modal.service";
import { OrderService } from "../../core/services/order.service";
import { PremiumService } from "../../loyalty/premium/premium.service";
import { IconNameType } from "../icons/icon-name-type.enum";
import { RoundIconComponent } from "../round-icon/round-icon.component";
import { PlanPremiumSingleComponent } from "./plan-premium-single/plan-premium-single.component";
import { PlanPremiumMultiComponent } from "./plan-premium-multi/plan-premium-multi.component";
import { A11yModule } from "@angular/cdk/a11y";
import { RouterLink } from "@angular/router";
import { StoreIdService } from "../../core/services/store-id.service";
import { LoyaltyService } from "../../core/services/loyalty.service";

@Component({
  selector: 'cwo-premium-promo-modal',
  standalone: true,
  imports: [
    CommonModule,
    RoundIconComponent,
    TranslocoDirective,
    ReactiveFormsModule,
    PlanPremiumSingleComponent,
    PlanPremiumMultiComponent,
    A11yModule,
    RouterLink
],
  templateUrl: './premium-promo-modal.component.html',
  styleUrl: './premium-promo-modal.component.scss',
})
export class PremiumPromoModalComponent {
  private readonly modalService = inject(ModalService);
  private readonly translocoService = inject(TranslocoService);
  private readonly premiumService = inject(PremiumService);
  private readonly orderService = inject(OrderService);
  private readonly loyaltyService = inject(LoyaltyService);
  readonly storeIdService = inject(StoreIdService);

  readonly iconNameType = IconNameType;

  readonly selectedSubscription = signal<LoyaltySubscription | null>(null);

  readonly selectedPlan = computed(() => this.selectedSubscription() ?? (this.subscriptions().length === 1 ? this.subscriptions()[0] ?? null : null));
  readonly selectedPlanName = computed(() => this.selectedPlan()?.subscriptionName ?? '');

  private readonly savingsCache = new Map<string, SubscriptionSavings | null>();

  private readonly savingsParams = computed(() => {
    const plan = this.selectedPlanName();
    if (!plan) return undefined;
    const order = this.orderService.order();
    return { plan, orderType: order?.orderType ?? '', ordersubTotal: order?.subTotal ?? 0 };
  });

  private readonly savingsResource = rxResource({
    params: () => this.savingsParams(),
    stream: ({ params }) => {
      const key = `${params.plan}|${params.orderType}|${params.ordersubTotal}`;
      const cached = this.savingsCache.get(key);
      if (cached !== undefined) return of(cached);
      return this.loyaltyService
        .getUpgradedSubscriptionSavings(params.plan)
        .pipe(tap((savings) => this.savingsCache.set(key, savings)));
    },
  });

  readonly subscriptions = this.premiumService.subscriptions;
  readonly isSinglePlan = computed(() => this.subscriptions().length === 1);

  private readonly savingsAmount = computed(() => this.savingsResource.value()?.savings ?? 0);
  private readonly orderDue = computed(() => (this.orderService.order()?.due ?? 0));
  private readonly discountedTotal = computed(() => Math.max(this.orderDue() - this.savingsAmount()));

  readonly savingsHeadline = computed(() => {
    if(this.savingsAmount() <= 0) return '';
    const plan = this.selectedPlan();
    return this.translocoService.translate('modals.premium_promo.savings_headline', {
      savings: this.formatDollars(this.savingsAmount()),
      plan: plan?.displayName || plan?.subscriptionName || '',
    });
  });

  readonly upgradeSummary = computed(() => {
    if(this.savingsAmount() <= 0) return '';
    const plan = this.selectedPlan();
    return this.translocoService.translate('modals.premium_promo.upgrade_summary', {
      plan: plan?.displayName || plan?.subscriptionName || '',
      discountedTotal: this.formatDollars(this.discountedTotal()),
      originalTotal: this.formatDollars(this.orderDue()),
    });
  });

  readonly ctaLabel = computed(() =>
    // Let's implement this when the backend supports the order & subscription payment
    // this.translocoService.translate('modals.premium_promo.cta_label', {
    //   total: this.formatDollars(this.discountedTotal() + this.orderDue() - this.savingsAmount()),
    // }),
    this.translocoService.translate('modals.premium_promo.cta_choose_plan')
  );

  private formatDollars(amount: number): string {
    return `$${amount.toFixed(2)}`;
  }

  closeModal(): void {
    this.modalService.closeModal(ModalName.PremiumPromo);
  }

  selectSubscription(subscription: LoyaltySubscription): void {
    this.selectedSubscription.set(subscription);
    this.premiumService.auditSubscriptionSelected(subscription);
  }
}